import React from 'react';
import {
  Box,
  TableRow,
  TableCell,
  Stack,
  Typography,
  Fade,
  Chip,
  Button,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  Receipt as EntryIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  UploadFile as UploadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const EmptyState = ({
  type,
  isLoading,
  searchTerm,
  filterType,
  message,
  onClearSearch,
  onClearFilters,
  onUploadStatement,
  onRefresh,
}) => {
  const theme = useTheme();

  const getIconAndColors = () => {
    switch (type) {
      case 'transactions':
        return {
          icon: <BankIcon sx={{ fontSize: 56, color: 'primary.main' }} />,
          gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          bgColor: alpha(theme.palette.primary.main, 0.04),
        };
      case 'journalEntries':
        return {
          icon: <EntryIcon sx={{ fontSize: 56, color: 'secondary.main' }} />,
          gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          bgColor: alpha(theme.palette.secondary.main, 0.04),
        };
      default:
        return {
          icon: <SearchIcon sx={{ fontSize: 56, color: 'grey.400' }} />,
          gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
          bgColor: alpha(theme.palette.grey[400], 0.04),
        };
    }
  };

  const getContent = () => {
    const { icon, gradient } = getIconAndColors();

    if (isLoading) {
      return {
        icon: <CircularProgress size={48} sx={{ color: 'primary.main' }} />,
        title: type === 'transactions' ? 'Carregando transações' : 'Carregando lançamentos',
        subtitle: 'Aguarde enquanto buscamos os dados...',
        actions: null,
      };
    }

    if (message) {
      return {
        icon,
        title: message,
        subtitle: null,
        actions: null,
      };
    }

    // No search or filter applied
    if (!searchTerm && !filterType) {
      if (type === 'transactions') {
        return {
          icon,
          title: 'Nenhuma transação disponível',
          subtitle: 'Carregue um extrato bancário para visualizar as transações',
          actions: onUploadStatement ? (
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={onUploadStatement}
              sx={{
                background: gradient,
                color: 'white',
                '&:hover': {
                  background: gradient,
                  opacity: 0.9,
                },
                borderRadius: 2,
                px: 3,
                py: 1.5,
              }}
            >
              Carregar Extrato
            </Button>
          ) : null,
        };
      } else {
        return {
          icon,
          title: 'Nenhum lançamento encontrado',
          subtitle: 'Os lançamentos aparecerão aqui quando disponíveis',
          actions: onRefresh ? (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
              }}
            >
              Atualizar
            </Button>
          ) : null,
        };
      }
    }

    // Search or filter applied but no results
    return {
      icon: <SearchIcon sx={{ fontSize: 56, color: 'warning.main' }} />,
      title: 'Nenhum resultado encontrado',
      subtitle: 'Tente ajustar os filtros ou termo de busca',
      actions: (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {searchTerm && onClearSearch && (
            <Chip
              label={`Busca: "${searchTerm}"`}
              onDelete={onClearSearch}
              variant="outlined"
              size="small"
              deleteIcon={<SearchIcon />}
            />
          )}
          {filterType && onClearFilters && (
            <Chip
              label={`Filtro: ${filterType === 'entrada' ? 'Entradas' : 'Saídas'}`}
              onDelete={onClearFilters}
              variant="outlined"
              size="small"
              deleteIcon={<FilterIcon />}
            />
          )}
        </Stack>
      ),
    };
  };

  const { icon, title, subtitle, actions } = getContent();
  const { bgColor } = getIconAndColors();

  return (
    <TableRow>
      <TableCell colSpan={6} align="center">
        <Fade in timeout={300}>
          <Box
            sx={{
              py: { xs: 4, md: 6 },
              px: { xs: 2, md: 4 },
              backgroundColor: bgColor,
              borderRadius: 2,
              mx: { xs: 1, md: 2 },
              my: 2,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: theme.palette.primary.main,
                borderRadius: '2px 2px 0 0',
              },
            }}
          >
            <Stack alignItems="center" spacing={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '50%',
                  backgroundColor: 'background.paper',
                  boxShadow: theme.shadows[4],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: -4,
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                    zIndex: -1,
                  },
                }}
              >
                {icon}
              </Box>

              <Stack alignItems="center" spacing={1}>
                <Typography
                  variant="h6"
                  color="text.primary"
                  fontWeight={600}
                  textAlign="center"
                  sx={{
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                  }}
                >
                  {title}
                </Typography>

                {subtitle && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{
                      maxWidth: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Stack>

              {actions && <Box sx={{ mt: 2 }}>{actions}</Box>}
            </Stack>
          </Box>
        </Fade>
      </TableCell>
    </TableRow>
  );
};

export default EmptyState;
